import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import versionService from '../services/versionService';
import { logger } from '../utils/logger';
import { useAuth } from './AuthContext';

// Create the context
export const VersionContext = createContext();

// Provider component
export const VersionProvider = ({ children }) => {
  const [versionStatus, setVersionStatus] = useState(null);
  const [versionLogs, setVersionLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get current user from AuthContext
  const { currentUser } = useAuth();

  // Function to fetch version status - memoized
  const fetchVersionStatus = useCallback(async () => {
    try {
      setLoading(true);
      logger.info('[VERSION CONTEXT]', 'Fetching version status...');

      // Use direct API call to status endpoint
      const response = await fetch('/api/system/version/status');
      const data = await response.json();

      logger.info('[VERSION CONTEXT]', 'Version status:', data);
      setVersionStatus(data);

      // Also load version logs
      const logs = await versionService.getVersionLogs();
      setVersionLogs(logs || []);

      setError(null);
    } catch (err) {
      logger.error('[VERSION CONTEXT]', 'Error fetching version status:', err);
      setError('Failed to load version status');
    } finally {
      setLoading(false);
    }
  }, []);

  // Function to log a version activity - memoized
  const logVersionActivity = useCallback(async (version, description, userId = null) => {
    try {
      logger.info('[VERSION CONTEXT]', 'Logging version activity:', version, description);
      await versionService.saveVersionLogEntry(version, description, userId);

      // Refresh logs after adding new entry
      const logs = await versionService.getVersionLogs();
      setVersionLogs(logs || []);

      return true;
    } catch (err) {
      logger.error('[VERSION CONTEXT]', 'Error logging version activity:', err);
      return false;
    }
  }, []);

  // Function to save user CV version - memoized
  const saveUserCVVersion = useCallback(async (userId, data, author) => {
    // Use current user's email or System if no user
    if (!author) {
      author = currentUser?.email || 'System';
    }

    try {
      logger.info('VERSION_CONTEXT', `Saving CV version for user ${userId}, author: ${author}`);
      const result = await versionService.saveVersionedData(userId, 'cv_analyzer', 'result.json', data, author);
      logger.info('VERSION_CONTEXT', `CV version created: ${result?.version_created}`);
      return result;
    } catch (error) {
      logger.error('VERSION_CONTEXT', `Error saving CV version for user ${userId}`, { error: error.message });
      return null;
    }
  }, [currentUser]);

  // Function to save user job skills version - memoized
  const saveUserJobSkillsVersion = useCallback(async (userId, data, author) => {
    // Use current user's email or System if no user
    if (!author) {
      author = currentUser?.email || 'System';
    }

    try {
      logger.info('VERSION_CONTEXT', `Saving job skills version for user ${userId}, author: ${author}`);
      const result = await versionService.saveVersionedData(userId, 'job_analyzer', 'result.json', data, author);
      logger.info('VERSION_CONTEXT', `Job skills version created: ${result?.version_created}`);
      return result;
    } catch (error) {
      logger.error('VERSION_CONTEXT', `Error saving job skills version for user ${userId}`, { error: error.message });
      return null;
    }
  }, [currentUser]);

  // Function to save user company version - memoized
  const saveUserCompanyVersion = useCallback(async (userId, data, author) => {
    // Use current user's email or System if no user
    if (!author) {
      author = currentUser?.email || 'System';
    }

    try {
      logger.info('VERSION_CONTEXT', `Saving company version for user ${userId}, author: ${author}`);
      const result = await versionService.saveVersionedData(userId, 'company_analyzer', 'result.json', data, author);
      logger.info('VERSION_CONTEXT', `Company version created: ${result?.version_created}`);
      return result;
    } catch (error) {
      logger.error('VERSION_CONTEXT', `Error saving company version for user ${userId}`, { error: error.message });
      return null;
    }
  }, [currentUser]);

  // Function to save user training recommendations version - memoized
  const saveUserTrainingRecommendationsVersion = useCallback(async (userId, data, author) => {
    // Use current user's email or System if no user
    if (!author) {
      author = currentUser?.email || 'System';
    }

    try {
      logger.info('VERSION_CONTEXT', `Saving training recommendations version for user ${userId}, author: ${author}`);
      const result = await versionService.saveVersionedData(userId, 'training_recommendations', 'result.json', data, author);
      logger.info('VERSION_CONTEXT', `Training recommendations version created: ${result?.version_created}`);
      return result;
    } catch (error) {
      logger.error('VERSION_CONTEXT', `Error saving training recommendations version for user ${userId}`, { error: error.message });
      return null;
    }
  }, [currentUser]);

  // Load data on initial mount
  useEffect(() => {
    fetchVersionStatus();
  }, [fetchVersionStatus]); // Now with proper dependency

  return (
    <VersionContext.Provider
      value={{
        versionStatus,
        versionLogs,
        loading,
        error,
        currentUser,
        fetchVersionStatus,
        logVersionActivity,
        saveUserCVVersion,
        saveUserJobSkillsVersion,
        saveUserCompanyVersion,
        saveUserTrainingRecommendationsVersion
      }}
    >
      {children}
    </VersionContext.Provider>
  );
};

// Custom hook for using the version context
export const useVersion = () => useContext(VersionContext);
